import * as React from 'react'

import { Box, NumberBadge, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceCompanyEmployeeScoreScaleSelectorFragment,
  PerformanceSetCompanyEmployeeScoreScaleDocument,
} from 'generated/resources/graphql'
import { maxBy, minBy } from 'lodash'

import SelectField from 'modules/core/components/SelectField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Props = {
  companyId: string
  currentScaleId?: string
  scoreScales: PerformanceCompanyEmployeeScoreScaleSelectorFragment[]
}

export const CompanyEmployeeScoreScaleSelector: React.FC<Props> = ({
  companyId,
  currentScaleId,
  scoreScales,
}) => {
  const [selectedScoreScaleId, setSelectedScoreScaleId] = React.useState(currentScaleId)
  const selectedScoreScale = scoreScales.find(({ id }) => id === selectedScoreScaleId)
  const scoreScaleOptions = scoreScales.map(({ id, scale, isDefault }) => {
    const min = minBy(scale, 'value')?.value
    const max = maxBy(scale, 'value')?.value
    const recommended = isDefault
      ? i18n.t('performance_process.employee_score.company_settings.recommended')
      : ''
    const label = i18n.t('performance_process.employee_score.company_settings.range_label', {
      min: min,
      max: max,
    })

    return {
      value: id,
      label: `${label} ${recommended}`,
    }
  })

  const [updateScale, { loading }] = useMutation(PerformanceSetCompanyEmployeeScoreScaleDocument, {
    extractorFn: (data) => data.performanceMutations.setPerformanceCompanyEmployeeScoreScale,
  })

  const changeSelectedScale = async (value: string) => {
    const previousScale = selectedScoreScaleId
    const scaleId = String(value)
    setSelectedScoreScaleId(scaleId)

    const response = await updateScale({
      variables: {
        companyId,
        scaleId,
      },
    })

    const result = handleResult(response, () => {})

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.employee_score.company_settings.updated_successfully'),
      })
    } else {
      setSelectedScoreScaleId(previousScale)
    }
  }

  return (
    <Box gap="s32">
      <SelectField
        loading={loading}
        value={selectedScoreScaleId?.toString()}
        label={i18n.t('performance_process.employee_score.company_settings.scale_selector')}
        options={scoreScaleOptions}
        onChange={(value) => changeSelectedScale(value)}
      />
      {selectedScoreScale && (
        <Box flexDirection="column" gap="s16">
          <Text.Normal size="200" weight="medium" color="grey1000">
            {i18n.t('performance_process.employee_score.company_settings.labels')}
          </Text.Normal>
          <Box flexDirection="column" gap="s16">
            {selectedScoreScale.scale.map(({ value, text }) => (
              <Box key={value} flexDirection="row" gap="s16" alignItems="center">
                <NumberBadge label={value} />
                <Text.Normal color="grey1000">{text}</Text.Normal>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}
